import React, { useState } from 'react';
import { useLens } from './LensProvider';

const PRESETS = [
  {
    name: 'Deep Meditation',
    description: 'High capacity, open intent, flexible narrative.',
    vignette: "You've been sitting for forty minutes. Your body is warm and heavy. Thoughts still arise but they pass like clouds — noticed, unnamed, released. Nothing feels urgent. The boundary between inside and outside has softened.",
    values: { B: 0.8, S: 0.7, N: 0.15, I: { openness: 0.9, fear: 0.05, care: 0.8, control: 0.05 } }
  },
  {
    name: 'Panic Attack',
    description: 'Low capacity, high fear, rigid narrative.',
    vignette: "Your heart is pounding. The room feels too bright, too close. Every thought loops back to the same place — something is wrong, something is wrong. You can't tell if the danger is real or if your body is lying to you.",
    values: { B: 0.3, S: 0.8, N: 0.85, I: { openness: 0.1, fear: 0.95, care: 0.2, control: 0.8 } }
  },
  {
    name: 'Psychedelic Peak',
    description: 'Senses wide open, narrative dissolved.',
    vignette: "Everything is breathing. The walls, the music, your own hands. The story of who you are has gone quiet and what remains is just... this. Overwhelming, beautiful, terrifying, hilarious — all at once, with no one to organize it.",
    values: { B: 0.5, S: 0.95, N: 0.1, I: { openness: 0.9, fear: 0.3, care: 0.5, control: 0.05 } }
  },
  {
    name: 'Creative Flow',
    description: 'Balanced capacity, high openness and care.',
    vignette: "The words are coming faster than you can type. You're not thinking about what to say — it's arriving, fully formed, from somewhere you can't quite locate. The clock has disappeared. You'll look up and two hours will have passed.",
    values: { B: 0.7, S: 0.6, N: 0.3, I: { openness: 0.85, fear: 0.1, care: 0.7, control: 0.1 } }
  },
  {
    name: 'Grief',
    description: 'Moderate capacity, emotional openness.',
    vignette: "A song comes on and suddenly you're crying. Not because anything new happened — the loss is old — but because your body decided it was time to feel it again. Everything is tender. Even kindness from strangers threatens to undo you.",
    values: { B: 0.4, S: 0.5, N: 0.6, I: { openness: 0.6, fear: 0.4, care: 0.9, control: 0.2 } }
  },
  {
    name: 'Default',
    description: 'Calm, open starting position.',
    vignette: 'A quiet morning. Coffee in hand. Nothing is pressing. The world feels manageable, even pleasant. You have room to receive whatever comes next.',
    values: { B: 0.7, S: 0.6, N: 0.3, I: { openness: 0.7, fear: 0.2, care: 0.6, control: 0.2 } }
  }
];

const Presets = () => {
  const { setB, setS, setN, setI } = useLens();
  const [activeVignette, setActiveVignette] = useState(null);

  const applyPreset = (preset) => {
    setB(preset.values.B);
    setS(preset.values.S);
    setN(preset.values.N);
    setI(preset.values.I);
    setActiveVignette(preset.name === activeVignette ? null : preset.name);
  };

  const activePreset = PRESETS.find(p => p.name === activeVignette);

  return (
    <div className="presets">
      <h3>Experience Presets</h3>
      <p className="presets-note">Same field, different lenses.</p>
      <div className="presets-grid">
        {PRESETS.map((preset) => (
          <button
            key={preset.name}
            className={`preset-button ${activeVignette === preset.name ? 'preset-active' : ''}`}
            onClick={() => applyPreset(preset)}
          >
            <span className="preset-name">{preset.name}</span>
            <span className="preset-desc">{preset.description}</span>
          </button>
        ))}
      </div>
      {activePreset && (
        <div className="preset-vignette">
          {activePreset.vignette}
        </div>
      )}
    </div>
  );
};

export default Presets;
